import { Phone, MapPin, Clock, AlertCircle, ArrowUpRight, Award } from "lucide-react";
import { Seo, physicianSchema } from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SITE } from "@/site";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.domain}/` },
    { "@type": "ListItem", position: 2, name: "Contact", item: `${SITE.domain}/contact` },
  ],
};

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact & Directions — Dr. Dixit, Gastroenterologist in Lanka, Varanasi"
        description="Book an appointment with Dr. Dixit, DM (Gastro) IMS BHU. Call +91 93696 98080, Mon–Sun 9 AM–5 PM. Clinic at Rashmi Nagar Colony, behind V2 Mall, Lanka, Varanasi – 221005."
        path="/contact"
        jsonLd={[physicianSchema, breadcrumbSchema]}
      />

      <section className="hero-grid-lines bg-gradient-to-b from-white to-sky-50/60">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20 text-center">
          <p
            data-testid="contact-eyebrow"
            className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600"
          >
            Contact &amp; Appointments
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-navy">
            <MaskedLine delay={0.1}>Call for an</MaskedLine>
            <MaskedLine delay={0.25}>
              <span className="text-sky-600">appointment.</span>
            </MaskedLine>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Consultations are by prior appointment. Call the number below and
            a convenient time will be fixed for you.
          </p>
          <p className="mx-auto mt-2 max-w-xl font-hindi text-base text-slate-500">
            अपॉइंटमेंट के लिए सीधे इस नंबर पर कॉल करें।
          </p>

          <Reveal delay={0.35}>
            <a
              href={SITE.phoneHref}
              data-testid="contact-big-phone-link"
              className="mt-10 inline-block font-heading text-4xl sm:text-6xl font-extrabold tracking-tight text-navy transition-colors hover:text-sky-600"
            >
              {SITE.phoneDisplay}
            </a>
            <div className="mt-6">
              <a
                href={SITE.phoneHref}
                data-testid="contact-call-now-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-navy px-9 py-4 font-heading text-lg font-bold text-white transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5"
              >
                <Phone size={20} strokeWidth={2.5} />
                Call Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <Reveal>
            <div
              data-testid="contact-address-card"
              className="h-full rounded-2xl border border-slate-200 bg-white p-8"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                <MapPin size={22} />
              </div>
              <h2 className="mt-5 font-heading text-xl font-bold text-navy">
                Clinic Address
              </h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                {SITE.address}
              </p>
              <p className="mt-3 text-sm text-slate-500">
                Landmark: behind V2 Mall. Easy to reach from Lanka, Sunderpur
                and BHU.
              </p>
              <p className="mt-2 font-hindi text-sm text-slate-500">
                क्लिनिक लंका में V2 मॉल के ठीक पीछे है।
              </p>
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-sky-600 px-6 py-3 font-heading text-sm font-bold text-white transition-all duration-200 hover:bg-sky-500 hover:-translate-y-0.5"
              >
                Get Directions
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              data-testid="contact-hours-card"
              className="h-full rounded-2xl border border-slate-200 bg-white p-8"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                <Clock size={22} />
              </div>
              <h2 className="mt-5 font-heading text-xl font-bold text-navy">
                Clinic Timings
              </h2>
              <p className="mt-3 font-heading text-2xl font-bold text-navy">
                Mon–Sun
              </p>
              <p className="mt-1 text-lg font-semibold text-sky-700">
                9:00 AM – 5:00 PM
              </p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                The clinic is open all seven days. Consultations are by prior
                appointment, so please call and book your slot before visiting.
              </p>
              <p className="mt-2 font-hindi text-sm text-slate-500">
                सोमवार से रविवार, सुबह 9 बजे से शाम 5 बजे तक। आने से पहले कॉल
                करके समय ले लें।
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              data-testid="contact-doctor-card"
              className="h-full rounded-2xl border border-slate-200 bg-white p-8"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy-50 text-navy">
                <Award size={22} />
              </div>
              <h2 className="mt-5 font-heading text-xl font-bold text-navy">
                About Your Doctor
              </h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Dr. Dixit is a reputed and well-known gastroenterologist in
                Varanasi. He holds a DM in Gastroenterology from IMS BHU and
                is a senior gastro &amp; liver specialist trusted by patients
                across Purvanchal.
              </p>
              <p className="mt-2 font-hindi text-sm text-slate-500">
                डॉ. दीक्षित वाराणसी के प्रतिष्ठित गैस्ट्रो एवं लीवर विशेषज्ञ
                हैं।
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="contact-emergency-note"
            className="mt-5 flex gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6"
          >
            <AlertCircle size={22} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-sm sm:text-base leading-relaxed text-amber-900">
              <span className="font-semibold">In an emergency</span> — severe
              abdominal pain, vomiting blood, black stools or sudden jaundice
              — go directly to the nearest hospital emergency department
              instead of waiting for an appointment.{" "}
              <span className="font-hindi">
                आपात स्थिति में सीधे नज़दीकी अस्पताल जाएँ।
              </span>
            </p>
          </div>
        </Reveal>
      </section>
    </>
  );
}
